import type { ReactNode } from "react";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker as MuiTimePicker } from "@mui/x-date-pickers/TimePicker";
import { type Moment } from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn, getInputDefaultStyles } from "utils/styling";

import {
  getMuiFilledTextFieldSx,
  getTimePickerModalSx,
  MUI_FILLED_FIELD,
  muiFilledInputLabelClasses,
} from "../lib/muiFilledFieldStyles";
import { formatTime, parseTime } from "../lib/timeInputUtils";

export type TimePickerProps = {
  className?: string;
  disabled?: boolean;
  height?: string;
  label?: ReactNode;
  minuteStep?: number;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  value?: null | string;
  width?: number | string;
};

export const TimePicker = ({
  className,
  disabled,
  height = "4rem",
  label,
  minuteStep = 1,
  onChange,
  placeholder = "hh:mm aa",
  readOnly,
  value,
}: TimePickerProps) => {
  const momentValue = useMemo(() => parseTime(value), [value]);
  const [pickerValue, setPickerValue] = useState<Moment | null>(() => momentValue);
  const isPickerOpenRef = useRef(false);

  useEffect(() => {
    setPickerValue((current) => {
      if (!momentValue && !current) return current;
      if (momentValue && current && momentValue.format("HH:mm") === current.format("HH:mm")) return current;
      if (isPickerOpenRef.current) return current;
      return momentValue;
    });
  }, [momentValue]);

  const commitValue = (nextValue: Moment | null) => {
    const formatted = formatTime(nextValue);
    if (formatted) onChange?.(formatted);
  };

  const handleValueChange = (nextValue: Moment | null) => {
    if (!isPickerOpenRef.current) commitValue(nextValue);
  };

  const hasValue = Boolean(pickerValue?.isValid());
  const { labelColor, textColor } = getInputDefaultStyles({ disabled, isEmpty: !hasValue });

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <span className={cn("relative block w-full [&_.MuiFilledInput-input:focus]:bg-transparent", className)}>
        <MuiTimePicker
          ampm
          closeOnSelect={false}
          disabled={disabled}
          minutesStep={minuteStep}
          onAccept={commitValue}
          onChange={handleValueChange}
          onClose={() => {
            isPickerOpenRef.current = false;
            setPickerValue(momentValue);
          }}
          onOpen={() => {
            isPickerOpenRef.current = true;
          }}
          readOnly={readOnly}
          slotProps={{
            actionBar: {
              actions: ["cancel", "accept"],
              className: cn(
                "mt-2 flex justify-end gap-1 px-1.5 py-1",
                "[&_.MuiButton-root]:text-lds-link-primary [&_.MuiButton-root]:font-ds-body-semibold-sm"
              ),
            },
            layout: {
              sx: getTimePickerModalSx(),
            },
            textField: {
              className: "font-ds-body-regular-md",
              fullWidth: true,
              InputLabelProps: {
                classes: {
                  root: muiFilledInputLabelClasses.root,
                  shrink: muiFilledInputLabelClasses.shrink,
                },
                shrink: Boolean(label),
                style: { color: labelColor },
              },
              inputProps: {
                className: "font-ds-body-regular-md mt-3",
                placeholder,
              },
              label,
              sx: getMuiFilledTextFieldSx({
                borderColor: hasValue ? MUI_FILLED_FIELD.borderAccent : MUI_FILLED_FIELD.borderStrong,
                disabledTextColor: MUI_FILLED_FIELD.fgMuted,
                height,
                textColor,
              }),
              variant: "filled",
            },
          }}
          timeSteps={{ hours: 1, minutes: minuteStep }}
          value={pickerValue}
        />
      </span>
    </LocalizationProvider>
  );
};
